import React, {
    createContext, useContext, useState, useCallback, useRef, useEffect, useMemo,
} from 'react';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { FormikProps } from 'formik';

import { useAuthContext } from 'contexts/AuthContext';
import { NSSlideout, NSButton } from 'bricks';
import DealScenarioDropdown from 'components/DealScenarioDropdown/DealScenarioDropdown';
import { BOESlideoutFormValues } from 'views/DealDashboard/BOE/components/SlideoutForm/types';
import { getDefaultValues, mapScenarioToFormValues } from 'views/DealDashboard/BOE/helper';
import BOESlideoutForm from 'views/DealDashboard/BOE/components/SlideoutForm/BOESlideoutForm';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { useDeleteBackOfEnvelope } from 'views/DealDashboard/BOE/components/SlideoutForm/hooks/useDeleteBOE';
import { IOptionAnyValue } from 'bricks/types';
import { useGetBackOfEnvelope } from 'views/DealDashboard/BOE/hooks/useGetBOE';
import { INestedOptionAnyValue } from 'components/DealScenarioDropdown/types';
import { useGetBackOfEnvelopeDeals } from 'views/DealDashboard/BOE/hooks/useGetBOEDeals';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import SaveModal from 'views/DealDashboard/BOE/components/SaveModal/SaveModal';
import LoanSizingModal from 'views/DealDashboard/BOE/components/LoanTestModal/LoanSizingModal';
import { ILoanSizingOption, ILoanSizingValue } from 'views/DealDashboard/BOE/components/LoanTestModal/types';
import { useGetAssetTypes } from 'hooks/useGetAssetTypes';
import './BackOfEnvelopeContext.scss';
import useToast from 'hooks/useToast';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { ILocallyStoredActiveTabName } from 'views/DealDashboard/types';
import { useProFormaModels } from 'queries/ProForma';
import { URLS } from 'services/urls';

interface BackOfEnvelopeContextProps {
    formikRef: React.RefObject<any>;
    isOpen: boolean;
    initialValues: BOESlideoutFormValues | null;
    toggleSlideout: (scenarioId?: number) => void;
    resetFormValues: () => void;
    setHasEditingValues: (editing: boolean) => void;
    setInitialValues: (values: BOESlideoutFormValues | null) => void;
    setSelectedScenarioId: (id: number | undefined) => void;
    setSelectedSlideoutScenarioId: (id: number | undefined) => void;
    setSelectedScenario: (scenario: IOptionAnyValue | undefined) => void;
    getSlideoutValues: any;
    selectedScenarioId: number | undefined;
    selectedScenario: IOptionAnyValue | undefined;
    slideoutScenarioData: any;
}

export const BackOfEnvelopeContext = createContext<BackOfEnvelopeContextProps | undefined>(undefined);

export const useSlideout = () => {
    const context = useContext(BackOfEnvelopeContext);
    if (!context) {
        throw new Error('useSlideout must be used within a SlideoutProvider');
    }
    return context;
};

const BackOfEnvelopeDataProvider: React.FC<{
    children: (data: {
        dropdownDeals: any;
        dropdownScenarios: any;
        overviewScenarioData: any;
        refetchSlideout: () => Promise<any>;
        combinedScenarios: any[];
        slideoutScenarioData: any;
    }) => React.ReactNode;
    orgId: string;
    selectedSlideoutScenarioId?: number;
    selectedScenarioId?: number;
    isOpen: boolean;
}> = ({
    children, orgId, selectedSlideoutScenarioId, selectedScenarioId, isOpen,
}) => {
    const { data: dropdownDeals } = useGetBackOfEnvelopeDeals({
        orgId,
        enabled: isOpen,
    });
    const { data: dropdownScenarios } = useGetBackOfEnvelopeScenarios({
        orgId,
        enabled: isOpen,
    });
    const { refetch: refetchSlideout, data: slideoutScenarioData } = useGetBackOfEnvelope({
        orgId,
        scenarioId: selectedSlideoutScenarioId,
        isForm: true,
        enabled: isOpen && !!selectedSlideoutScenarioId,
    });
    const { data: overviewScenarioData } = useGetBackOfEnvelope({
        orgId,
        scenarioId: selectedScenarioId,
        enabled: isOpen && !!selectedScenarioId,
    });

    const simplifiedScenarios = useMemo(() => {
        if (!dropdownDeals?.items) return [];
        return dropdownDeals.items.map(item => ({
            name: item.name,
            id: item.id,
            scenarios: [{ id: -1, name: 'default' }],
            hasNestedScenarios: true,
        }));
    }, [dropdownDeals]);

    const combinedScenarios = useMemo(() => [
        ...simplifiedScenarios,
        ...(dropdownScenarios?.items?.map(item => ({
            name: item.name,
            id: item.id,
        })) || []),
    ], [simplifiedScenarios, dropdownScenarios]);

    return children({
        dropdownDeals,
        dropdownScenarios,
        overviewScenarioData,
        refetchSlideout,
        combinedScenarios,
        slideoutScenarioData,
    });
};

export const BackOfEnvelopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { selectedOrganizationId } = useAuthContext();
    const { showSuccess } = useToast();

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showLoanSizingModal, setShowLoanSizingModal] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [hasEditingValues, setHasEditingValues] = useState(false);
    const [isCreatingNewScenario, setIsCreatingNewScenario] = useState(false);
    const [selectedSlideoutScenarioId, setSelectedSlideoutScenarioId] = useState<number | undefined>(undefined);
    const [initialValues, setInitialValues] = useState<BOESlideoutFormValues | null>(null);
    const [selectedScenarioId, setSelectedScenarioId] = useState<number | undefined>(undefined);
    const [selectedScenario, setSelectedScenario] = useState<IOptionAnyValue | undefined>(undefined);
    const [selectedDealForModal, setSelectedDealForModal] = useState<number | null>(null);

    const { dealId } = useParams();
    const queryClient = useQueryClient();

    const { mutate: deleteScenario } = useDeleteBackOfEnvelope();

    const { data: dropdownScenarios } = useGetBackOfEnvelopeScenarios({
        orgId: selectedOrganizationId!,
        enabled: isOpen,
    });

    const { data: slideoutScenarioData } = useGetBackOfEnvelope({
        orgId: selectedOrganizationId!,
        scenarioId: selectedSlideoutScenarioId,
        isForm: true,
        enabled: isOpen && !!selectedSlideoutScenarioId,
    });

    const { data: overviewScenarioData } = useGetBackOfEnvelope({
        orgId: selectedOrganizationId!,
        scenarioId: selectedScenarioId,
        enabled: isOpen && !!selectedScenarioId,
    });

    const { data: fetchedDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const [, setLocallyStoredActiveTabName] = useLocalStorage<ILocallyStoredActiveTabName>(
        PageSpecificKeys.DealDashboardPage,
        {},
    );

    const { data: assetTypeValuesRaw } = useGetAssetTypes();
    const { data: proFormaModelTypes } = useProFormaModels();
    const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    const proFormaModelOptions = useMemo(
        () => proFormaModelTypes?.map(assetType => ({
            value: String(assetType.id),
            label: capitalizeFirstLetter(assetType.name),
        })) || [],
        [proFormaModelTypes],
    );

    const dealAssetTypeName = assetTypeValuesRaw?.find(item => item.correlationId === fetchedDeal?.assetType?.correlationId)?.name;
    const initialAssetTypeOption = proFormaModelOptions.find(item => item.label === dealAssetTypeName);
    const defaultValues = getDefaultValues({ assetTypes: proFormaModelOptions, initialAssetType: initialAssetTypeOption });

    const formikRef = useRef<FormikProps<BOESlideoutFormValues>>(null);

    const resetFormValues = () => {
        if (formikRef?.current) {
            formikRef.current.resetForm({ values: defaultValues });
            setHasEditingValues(false);
            setInitialValues(null);
        }
    };

    const toggleSlideout = useCallback((scenarioId?: number) => {
        setIsOpen(prev => {
            if (prev) {
                resetFormValues();
                setIsCreatingNewScenario(false);
            }

            if (!prev && (scenarioId === undefined || typeof scenarioId === 'number')) {
                setSelectedSlideoutScenarioId(scenarioId);
            } else {
                setSelectedSlideoutScenarioId(undefined);
            }

            return !prev;
        });
    }, [resetFormValues]);

    const getSlideoutValues = () => {
        const formValues = mapScenarioToFormValues({
            scenario: overviewScenarioData || null,
            options: { assetTypes: proFormaModelOptions },
            initialAssetTypeOption,
        });

        if (formikRef?.current && formValues) {
            formikRef.current.setValues(formValues);
        } else {
            setInitialValues(formValues);
        }

        setHasEditingValues(true);
    };

    // Auto-open BOE from URL parameter (for cross-app navigation from Core NavBar)
    useEffect(() => {
        const isDealsRoute = window.location.pathname === URLS.DEALS.HOME;
        const params = new URLSearchParams(window.location.search);
        const shouldOpenBOE = isDealsRoute && params.get('openBOE') === 'true';

        if (shouldOpenBOE && !isOpen) {
            // Need to wait for UI to slideout and be mounted so formik ref is available in 2nd useEffect
            toggleSlideout();
        }
    }, [isOpen, toggleSlideout]);

    useEffect(() => {
        const isFormikInitialized = !!formikRef?.current;
        const isDealsRoute = window.location.pathname === URLS.DEALS.HOME;
        const params = new URLSearchParams(window.location.search);
        const shouldSetValues = isFormikInitialized && isOpen && isDealsRoute && params.get('openBOE') === 'true';

        if (shouldSetValues) {
            const defaultValues = getDefaultValues({ assetTypes: proFormaModelOptions });
            formikRef.current.setValues(defaultValues);

            // Clean up openBOE URL parameter
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [isOpen, proFormaModelOptions, formikRef?.current]);

    const handleSave = async () => {
        if (formikRef.current) {
            if (hasEditingValues) {
                const selectedScenarioInUncategorized = dropdownScenarios?.items?.find(scenario => scenario.id === selectedSlideoutScenarioId);
                if (selectedScenarioInUncategorized) {
                    setSelectedDealForModal(Number(dealId));
                    setShowSaveModal(true);
                } else {
                    formikRef.current.submitForm();
                }
                return;
            }

            const errors = await formikRef.current.validateForm();
            if (Object.keys(errors).length === 0) {
                setSelectedDealForModal(Number(dealId));
                setShowSaveModal(true);
            } else {
                formikRef.current.setTouched(Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
            }
        }
    };

    const handleDelete = useCallback(() => {
        if (!formikRef.current?.values.id) return;

        deleteScenario(
            { orgId: selectedOrganizationId!, scenarioId: Number(formikRef.current?.values.id) },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['back-of-envelope-scenarios', selectedOrganizationId!, Number(dealId)] });
                    queryClient.invalidateQueries({ queryKey: ['back-of-envelope', selectedOrganizationId!] });
                    setSelectedScenarioId(undefined);
                    setSelectedScenario(undefined);

                    // Remove scenario from storage
                    setLocallyStoredActiveTabName(prev => {
                        const newState = { ...prev };
                        if (newState[fetchedDeal?.id as number]) {
                            delete newState[fetchedDeal?.id as number].scenarioId;
                        }
                        return newState;
                    });

                    toggleSlideout();
                    if (dropdownScenarios?.items && dropdownScenarios.items.length > 0) {
                        const firstScenario = dropdownScenarios.items[0];
                        setSelectedScenario({ label: firstScenario.name, value: firstScenario.id });
                        setSelectedSlideoutScenarioId(firstScenario.id);
                    } else {
                        setSelectedScenario(undefined);
                        setSelectedSlideoutScenarioId(undefined);
                    }
                    showSuccess('Scenario deleted successfully!');
                },
                onError: error => {
                    console.error('Error deleting scenario:', error);
                },
            },
        );
    }, [selectedSlideoutScenarioId, selectedScenarioId, fetchedDeal?.id, setLocallyStoredActiveTabName]);

    const handleSelectScenarioInSlideout = useCallback(
        (option: IOptionAnyValue) => {
            if (option.label === 'SEARCH' || option.label === 'CREATE' || option.label === 'BACK') {
                return;
            }

            const scenarioId = option.id || option.value;
            setSelectedSlideoutScenarioId(scenarioId);
        },
        [],
    );

    const handleCreateScenario = useCallback((option: INestedOptionAnyValue) => {
        if (formikRef?.current) {
            formikRef.current.setFieldValue('id', null);
        }
        setHasEditingValues(false);
        setIsCreatingNewScenario(true);
        setSelectedDealForModal(null);
        setSelectedScenarioId(undefined);
        setSelectedScenario(undefined);
        setSelectedSlideoutScenarioId(undefined);
        resetFormValues();
    }, []);

    useEffect(() => {
        if (!isCreatingNewScenario) {
            setHasEditingValues(!!formikRef?.current?.values.id);
        }
    }, [formikRef.current?.values, isOpen, isCreatingNewScenario]);

    useEffect(() => {
        if (slideoutScenarioData && formikRef.current && selectedSlideoutScenarioId) {
            const formValues = mapScenarioToFormValues({
                scenario: slideoutScenarioData,
                options: { assetTypes: proFormaModelOptions },
                initialAssetTypeOption,
            });

            if (formValues) {
                formikRef.current.setValues(formValues);
                setHasEditingValues(true);
            }
        }
    }, [slideoutScenarioData, formikRef.current, selectedSlideoutScenarioId]);

    const isEditing = useMemo(() => hasEditingValues && !isCreatingNewScenario, [hasEditingValues, isCreatingNewScenario]);

    const handleCancel = useCallback(() => {
        setShowSaveModal(false);
        setSelectedDealForModal(null);
        setIsCreatingNewScenario(false);

        if (formikRef.current) {
            formikRef.current.resetForm();
            setHasEditingValues(false);
            setInitialValues(null);
        }
    }, [formikRef, setHasEditingValues, setInitialValues]);

    return (
        <BackOfEnvelopeContext.Provider
            value={{
                formikRef,
                isOpen,
                initialValues,
                toggleSlideout,
                resetFormValues,
                setHasEditingValues,
                setInitialValues,
                setSelectedScenarioId,
                setSelectedSlideoutScenarioId,
                setSelectedScenario,
                selectedScenarioId,
                selectedScenario,
                getSlideoutValues,
                slideoutScenarioData,
            }}
        >
            {children}
            <BackOfEnvelopeDataProvider
                orgId={selectedOrganizationId!}
                selectedSlideoutScenarioId={selectedSlideoutScenarioId}
                selectedScenarioId={selectedScenarioId}
                isOpen={isOpen}
            >
                {({ combinedScenarios }) => (
                    <>
                        <NSSlideout
                            data-testid="ns-slideout"
                            key={isOpen ? 'slideout-open' : 'slideout-closed'}
                            modalHeaderText="Back-Of-Envelope"
                            modalHeaderSubtitleText="Quickly assess the financial feasibility of this deal."
                            modalHeaderTextClassName="text-dark mb-1"
                            modalHeaderSubtitleTextClassName="mt-0"
                            modalBodyClassName="pb-0 px-3 pt-3"
                            contentClassName="BackOfEnvelope__content"
                            customHeaderActions={(
                                <div className="d-flex">
                                    {combinedScenarios && combinedScenarios.length > 0 && (
                                        <DealScenarioDropdown
                                            preSelectScenario={selectedScenario || { label: initialValues?.name || '', value: initialValues?.id }}
                                            scenarios={combinedScenarios}
                                            handleCreateScenario={handleCreateScenario}
                                            handleSelectScenario={handleSelectScenarioInSlideout}
                                            setSelectedScenarioId={setSelectedSlideoutScenarioId}
                                            includeCreate={isEditing}
                                            includeLabels
                                            includeSearch
                                        />
                                    )}
                                    {isEditing ? (
                                        <>
                                            <NSButton
                                                color="danger"
                                                icon={faTrashCan}
                                                callback={handleDelete}
                                                className="mx-2 heap-deal-delete-back-of-envelope"
                                            />
                                            <NSButton color="primary" text="Save" callback={handleSave} className="heap-deal-save-back-of-envelope" />
                                        </>
                                    ) : (
                                        <>
                                            <NSButton
                                                color="secondary"
                                                text="Clear"
                                                className="mx-2 heap-deal-clear-back-of-envelope"
                                                callback={resetFormValues}
                                                allowReadOnlyAccess
                                            />
                                            <NSButton color="primary" text="Save" callback={handleSave} className="heap-deal-save-back-of-envelope" />
                                        </>
                                    )}
                                </div>
                            )}
                            isOpen={isOpen}
                            toggle={toggleSlideout}
                        >
                            <BOESlideoutForm
                                ref={formikRef}
                                initialValues={initialValues}
                                isEditing={isEditing}
                                setShowModal={() => { }}
                                setShowLoanSizingModal={() => setShowLoanSizingModal(true)}
                                deal={fetchedDeal}
                                orgId={selectedOrganizationId}
                                toggleSlideout={toggleSlideout}
                            />
                        </NSSlideout>
                        <SaveModal
                            selectedScenarioName={formikRef.current?.values?.name || ''}
                            dealId={selectedDealForModal}
                            toggle={() => {
                                setShowSaveModal(!showSaveModal);
                                setSelectedDealForModal(null);
                                setIsCreatingNewScenario(false);
                            }}
                            isOpen={showSaveModal}
                            onConfirm={(createNewDeal: boolean = false) => {
                                if (createNewDeal && formikRef.current) {
                                    formikRef.current.setFieldValue('createNewDeal', createNewDeal);
                                }
                                if (formikRef.current) {
                                    formikRef.current.submitForm();
                                }
                            }}
                            onCancel={handleCancel}
                            setFieldValue={formikRef.current?.setFieldValue}
                            modalHeaderText="Save scenario"
                            modalFooterButtonText="Go back to selection"
                            modalFooterCancelButtonText="Cancel"
                            backdrop="static"
                        />
                        <LoanSizingModal
                            boeValues={formikRef.current?.values}
                            toggle={() => setShowLoanSizingModal(!showLoanSizingModal)}
                            isOpen={showLoanSizingModal}
                            onConfirm={(option: ILoanSizingOption, type: DEVELOPMENT_SOURCE_METRIC, values: ILoanSizingValue) => {
                                // Reset market cap rate
                                formikRef.current?.setFieldValue('marketCapRate', 0);

                                formikRef.current?.setFieldValue('selectedLoanCalculationOptions', option);
                                formikRef.current?.setFieldValue('contructionFinancing', values.contructionFinancing);
                                if (type === DEVELOPMENT_SOURCE_METRIC.LTV) {
                                    formikRef.current?.setFieldValue('marketCapRate', values.marketCapRate);
                                }
                            }}
                            onCancel={() => {
                                setShowLoanSizingModal(false);
                            }}
                            backdrop="static"
                        />
                    </>
                )}
            </BackOfEnvelopeDataProvider>
        </BackOfEnvelopeContext.Provider>
    );
};
